import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { UserStatusConst } from 'src/app/Models/userStatus.const';
import {
  RegistrationDivision,
  RegistrationRequestService,
  RegistrationUser,
  UserRegistrationRequest,
} from 'src/app/Services/RegistrationRequest/registration-request.service';

@Component({
  standalone: false,
  selector: 'app-self-registration',
  templateUrl: './self-registration.component.html',
  styleUrls: ['./self-registration.component.scss'],
})
export class SelfRegistrationComponent implements OnInit, OnDestroy {
  readonly supportEmail = 'EWRSfeedback@ontario.ca';
  readonly maxDelegates = 5;

  managerSearchControl = new UntypedFormControl();
  divisionSearchControl = new UntypedFormControl();
  delegateSearchControl = new UntypedFormControl();

  manager: RegistrationUser;
  currentDivision: RegistrationDivision;
  delegateValue: RegistrationUser;

  searchedManagers: RegistrationUser[] = [];
  searchedDelegates: RegistrationUser[] = [];
  delegates: RegistrationUser[] = [];
  divisions: RegistrationDivision[] = [];
  allDivisions: RegistrationDivision[] = [];

  managerNoResultsLabel = '';
  delegateNoResultsLabel = '';
  divisionNoResultsLabel = 'No Results';
  delegateFeedback = '';
  managerValidationError = '';
  generalError = '';

  statusLoading = true;
  divisionsLoaded = false;
  submitting = false;
  saveSubmitted = false;
  justSubmitted = false;
  isChecked = false;
  registrationRequest: UserRegistrationRequest;

  private readonly subscriptions = new Subscription();
  private managerSearchSubscription?: Subscription;

  compareDivision = (a: RegistrationDivision, b: RegistrationDivision) => {
    if (!a || !b) {
      return a === b;
    }
    return a.id === b.id;
  };

  constructor(
    private registrationRequestService: RegistrationRequestService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadRegistrationStatus();
    this.loadDivisions();

    this.subscriptions.add(
      this.managerSearchControl.valueChanges
        .pipe(debounceTime(750))
        .subscribe((value) => this.searchForManager(value))
    );

    this.subscriptions.add(
      this.divisionSearchControl.valueChanges
        .pipe(debounceTime(750))
        .subscribe((value) => this.searchForDivision(value))
    );

    this.subscriptions.add(
      this.delegateSearchControl.valueChanges
        .pipe(debounceTime(750))
        .subscribe((value) => this.onDelegateSearchChanged(value))
    );
  }

  ngOnDestroy(): void {
    this.managerSearchSubscription?.unsubscribe();
    this.subscriptions.unsubscribe();
  }

  declarationChange(checked: boolean): void {
    this.isChecked = checked;
  }

  managerSelected(): void {
    this.managerValidationError = '';
    this.generalError = '';
    this.applyManagerDivision();
  }

  searchForManager(value: any): void {
    this.managerSearchSubscription?.unsubscribe();
    this.managerSearchSubscription = undefined;

    if (typeof value !== 'string') {
      return;
    }

    const search = value.trim();
    this.managerValidationError = '';

    if (!search) {
      this.managerNoResultsLabel = '';
      this.searchedManagers = this.manager ? [this.manager] : [];
      return;
    }

    this.managerNoResultsLabel = 'Searching...';
    this.searchedManagers = [];
    this.managerSearchSubscription = this.registrationRequestService
      .searchManagers(search)
      .subscribe({
        next: (data) => {
          this.searchedManagers = this.extractArray(data);
          if (this.manager && !this.findByEmail(this.searchedManagers, this.manager.email)) {
            this.searchedManagers.push(this.manager);
          }
          this.managerNoResultsLabel = this.searchedManagers.length ? '' : 'No Results';
        },
        error: () => {
          this.searchedManagers = [];
          this.managerNoResultsLabel = 'No Results';
          this.generalError = `Managers could not be loaded. Try again or contact ${this.supportEmail}.`;
        },
      });
  }

  searchForDivision(value: string): void {
    const search = (value || '').trim().toLowerCase();
    if (!this.divisionsLoaded) {
      this.loadDivisions();
      return;
    }
    if (!search) {
      this.divisions = this.allDivisions.slice();
      return;
    }
    this.divisions = this.allDivisions.filter((division) =>
      division.name?.toLowerCase().includes(search)
    );
  }

  onDivisionOpened(opened: boolean): void {
    if (!opened) {
      return;
    }
    if (!this.divisionsLoaded) {
      this.loadDivisions();
      return;
    }
    if (!this.divisionSearchControl?.value) {
      this.divisions = this.allDivisions.slice();
    }
  }

  onDelegateSearchChanged(value: any): void {
    if (typeof value !== 'string') {
      return;
    }

    this.delegateValue = null;
    this.searchForDelegate(value);
  }

  searchForDelegate(value: string): void {
    const search = (value || '').trim();

    if (!search) {
      this.delegateNoResultsLabel = '';
      this.searchedDelegates = [];
      return;
    }

    this.delegateNoResultsLabel = 'Searching...';
    this.registrationRequestService.searchDelegates(search).subscribe({
      next: (data) => {
        this.searchedDelegates = this.extractArray(data).filter(
          (delegate) => !this.findByEmail(this.delegates, delegate.email)
        );
        this.delegateNoResultsLabel = this.searchedDelegates.length ? '' : 'No Results';
      },
      error: () => {
        this.searchedDelegates = [];
        this.delegateNoResultsLabel = 'No Results';
        this.delegateFeedback = 'Delegates could not be loaded. Try again.';
      },
    });
  }

  delegateSelected(delegate: RegistrationUser): void {
    if (!delegate) {
      return;
    }

    this.delegateValue = delegate;
    this.delegateFeedback = '';
  }

  addDelegate(): void {
    if (!this.delegateValue?.email) {
      this.delegateFeedback = 'Select a delegate from the search results.';
      return;
    }

    if (this.delegates.length >= this.maxDelegates) {
      this.delegateFeedback = `You can add up to ${this.maxDelegates} delegates.`;
      return;
    }

    if (this.findByEmail(this.delegates, this.delegateValue.email)) {
      this.delegateFeedback = 'This delegate has already been added.';
      return;
    }

    this.delegates.push(this.delegateValue);
    this.clearDelegateSearch();
  }

  removeDelegate(index: number): void {
    this.delegates.splice(index, 1);
    this.delegateFeedback = '';
  }

  submit(): void {
    this.saveSubmitted = true;
    this.managerValidationError = '';
    this.generalError = '';
    this.delegateFeedback = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (!this.hasRequiredFields()) {
      return;
    }

    this.submitting = true;
    this.registrationRequestService
      .submit({
        managerEmail: this.manager.email,
        divisionId: this.currentDivision.id,
        delegateEmails: this.delegates.map((delegate) => delegate.email),
      })
      .subscribe({
        next: (data) => {
          this.submitting = false;
          this.justSubmitted = true;
          this.registrationRequest = data?.value;
        },
        error: (err: HttpErrorResponse) => {
          this.submitting = false;
          const message = this.getErrorMessage(err);

          if (message.toLowerCase().includes('valid manager')) {
            this.managerValidationError = message;
          } else {
            this.generalError = message;
          }
        },
      });
  }

  get confirmationTitle(): string {
    return this.justSubmitted ? 'Request submitted successfully!' : 'Application in progress';
  }

  get confirmationCopy(): string {
    return this.justSubmitted
      ? 'Your registration request has been sent to your manager for review and your manager will have 2 weeks to respond. You will receive an email notification once a decision has been made.'
      : 'Your application has been successfully submitted and is awaiting manager review. You will receive an email notification once a decision has been made.';
  }

  displayDelegate(delegate: RegistrationUser): string {
    return delegate?.name || '';
  }

  get disableDelegate(): boolean {
    return this.delegates.length >= this.maxDelegates;
  }

  get showManagerRequiredError(): boolean {
    return this.saveSubmitted && !this.manager?.email;
  }

  get showDivisionRequiredError(): boolean {
    return this.saveSubmitted && !this.currentDivision?.id;
  }

  get showDeclarationRequiredError(): boolean {
    return this.saveSubmitted && !this.isChecked;
  }

  private loadRegistrationStatus(): void {
    this.subscriptions.add(
      this.registrationRequestService.getStatus().subscribe({
        next: (data) => {
          this.statusLoading = false;
          const status = data?.value;

          if (status?.hasPendingRequest && status.request) {
            this.registrationRequest = status.request;
            return;
          }

          if (status?.userStatusId === UserStatusConst.UnRegistered) {
            this.router.navigateByUrl('/register');
            return;
          }

          if (status?.userStatusId === UserStatusConst.Active) {
            this.router.navigateByUrl('/');
          }
        },
        error: () => {
          this.statusLoading = false;
          this.generalError = `Registration status could not be loaded. Try again or contact ${this.supportEmail}.`;
        },
      })
    );
  }

  private loadDivisions(): void {
    this.subscriptions.add(
      this.registrationRequestService.getDivisions().subscribe({
        next: (data) => {
          this.allDivisions = this.sortDivisions(this.extractArray(data));
          this.divisions = this.allDivisions.slice();
          this.divisionsLoaded = true;
          this.applyManagerDivision();
        },
        error: () => {
          this.divisionsLoaded = false;
          this.generalError = `Divisions could not be loaded. Try again or contact ${this.supportEmail}.`;
        },
      })
    );
  }

  private hasRequiredFields(): boolean {
    return !!this.manager?.email && !!this.currentDivision?.id && this.isChecked;
  }

  private clearDelegateSearch(): void {
    this.delegateValue = null;
    this.searchedDelegates = [];
    this.delegateNoResultsLabel = '';
    this.delegateSearchControl.setValue('', { emitEvent: false });
  }

  private applyManagerDivision(): void {
    const managerDivisionId = this.manager?.divisionId;
    if (!managerDivisionId) {
      return;
    }

    const managerDivision = this.allDivisions.find((division) => division.id === managerDivisionId);
    if (managerDivision) {
      this.currentDivision = managerDivision;
    }
  }

  private findByEmail(users: RegistrationUser[], email: string): RegistrationUser {
    return users?.find((user) => user.email?.toLowerCase() === email?.toLowerCase());
  }

  private sortDivisions(divisions: RegistrationDivision[]): RegistrationDivision[] {
    return divisions.slice().sort((a, b) => (a.name > b.name ? 1 : b.name > a.name ? -1 : 0));
  }

  private extractArray<T>(data: { value?: T[]; valueobjs?: T[] }): T[] {
    const values = data?.valueobjs ?? data?.value ?? [];
    return Array.isArray(values) ? values : [];
  }

  private getErrorMessage(err: HttpErrorResponse): string {
    return err?.error?.message || err?.message || 'Registration request could not be submitted.';
  }
}
